import React, { useState, useRef } from "react";
import {
  View,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Animated,
} from "react-native";
// NOTE: SK-VOICE-FE uses `react-native-vector-icons/Ionicons`. SKCatalog ships
// its own SVG Icon (no native-font dependency), so the glyphs below are mapped:
//   search/search-outline → "search", close → "x", arrow-forward → "arrow-r".
// Every other line is identical to the source component.
import Icon from "./icon";
import { FontFamily } from "../theme/typography";
import { LightColors, DarkColors } from "../theme/colors";

/* ─────────────────────────────────────────
   Props
 ───────────────────────────────────────── */
type Props = Readonly<{
  placeholder?: string;
  value: string;
  onChange: (text: string) => void;
  onSubmit?: () => void;
  onFocus?: () => void;
  isDark?: boolean;
  autoFocus?: boolean;
}>;

/* ─────────────────────────────────────────
   Component
 ───────────────────────────────────────── */
export default function CommonSearch({
  placeholder = "Search…",
  value,
  onChange,
  onSubmit,
  onFocus,
  isDark = false,
  autoFocus = false,
}: Props) {
  const [focused, setFocused] = useState(false);

  /* Animated values */
  const iconScale  = useRef(new Animated.Value(1)).current;
  const clearScale = useRef(new Animated.Value(0)).current;

  const handleFocus = () => {
    setFocused(true);
    onFocus?.();
    Animated.spring(iconScale, {
      toValue: 1.15,
      useNativeDriver: true,
      tension: 280,
      friction: 18,
    }).start();
  };

  const handleBlur = () => {
    setFocused(false);
    Animated.spring(iconScale, {
      toValue: 1,
      useNativeDriver: true,
      tension: 280,
      friction: 18,
    }).start();
  };

  const handleChange = (text: string) => {
    onChange(text);
    Animated.spring(clearScale, {
      toValue: text.length > 0 ? 1 : 0,
      useNativeDriver: true,
      tension: 320,
      friction: 20,
    }).start();
  };

  const handleClear = () => {
    onChange("");
    Animated.spring(clearScale, {
      toValue: 0,
      useNativeDriver: true,
      tension: 320,
      friction: 20,
    }).start();
  };

  /* Theme tokens */
  const T = isDark ? dark : light;

  // Determine submit button background color
  let submitBgColor = T.submitBg;
  if (focused) {
    submitBgColor = T.activeBorder;
  }

  const getWrapperStyles = () => {
    const shadowStyle = (() => {
      if (Platform.OS !== "web") return {};

      if (focused) {
        return {
          boxShadow: isDark
            ? `0 0 0 3px rgba(59,91,246,0.22), 0 4px 24px rgba(0,0,0,0.4)`
            : `0 0 0 3px rgba(99,102,241,0.14), 0 4px 20px rgba(99,102,241,0.08)`
        };
      }

      return {
        boxShadow: isDark
          ? "0 2px 12px rgba(0,0,0,0.35)"
          : "0 2px 12px rgba(0,0,0,0.06)"
      };
    })();

    return [
      styles.wrapper,
      { backgroundColor: T.bg, borderColor: focused ? T.activeBorder : T.border },
      shadowStyle,
    ];
  };

  const wrapperStyle = getWrapperStyles();


  return (
    <View style={wrapperStyle}>
      {/* Search icon — animates on focus */}
      <Animated.View style={[styles.iconWrap, { transform: [{ scale: iconScale }] }]}>
        <Icon
          name={"search"}
          size={18}
          color={focused ? T.activeBorder : T.iconColor}
        />
      </Animated.View>

      {/* Input */}
      <TextInput
        autoFocus={autoFocus}
        value={value}
        onChangeText={handleChange}
        onSubmitEditing={onSubmit}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        placeholderTextColor={T.placeholder}
        returnKeyType="search"
        underlineColorAndroid="transparent"
        selectionColor={T.activeBorder}
        style={[
          styles.input,
          { color: T.text },
          Platform.OS === "web" && ({ outlineStyle: "none" } as any),
        ]}
      />

      {/* Right side actions */}
      <View style={styles.actions}>
        {/* Clear button — springs in/out */}
        <Animated.View style={{ transform: [{ scale: clearScale }], opacity: clearScale }}>
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
            activeOpacity={0.7}
            style={[styles.clearBtn, { backgroundColor: T.clearBg }]}
          >
            <Icon name="x" size={12} color={T.clearIcon} />
          </TouchableOpacity>
        </Animated.View>

        {/* Divider */}
        {value.length > 0 && (
          <View style={[styles.divider, { backgroundColor: T.divider }]} />
        )}

        {/* Submit arrow */}
        <TouchableOpacity
          onPress={onSubmit}
          activeOpacity={0.65}
          style={[
            styles.submitBtn,
            { backgroundColor: submitBgColor },
          ]}
        >
          <Icon
            name="arrow-r"
            size={14}
            color={focused ? T.submitIconActive : T.submitIcon}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
}

/* ─────────────────────────────────────────
   Theme tokens (derived from the shared palette)
───────────────────────────────────────── */
const light = {
  bg:           LightColors.card,
  border:       LightColors.lineStrong,
  activeBorder: LightColors.accent,
  text:         LightColors.ink,
  placeholder:  LightColors.ink4,
  iconColor:    LightColors.ink4,
  clearBg:      LightColors.paper2,
  clearIcon:    LightColors.ink3,
  divider:      LightColors.line,
  submitBg:     LightColors.paper2,
  submitIcon:   LightColors.ink4,
  submitIconActive: LightColors.white,
};

const dark = {
  bg:           DarkColors.card,
  border:       DarkColors.line,
  activeBorder: DarkColors.accent,
  text:         DarkColors.ink,
  placeholder:  DarkColors.ink4,
  iconColor:    DarkColors.ink4,
  clearBg:      DarkColors.paper2,
  clearIcon:    DarkColors.ink3,
  divider:      DarkColors.line,
  submitBg:     DarkColors.paper2,
  submitIcon:   DarkColors.ink3,
  submitIconActive: DarkColors.white,
};

/* ─────────────────────────────────────────
   Styles
───────────────────────────────────────── */
const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 42,
    borderRadius: 0,
    borderWidth: 1.5,
    paddingLeft: 14,
    paddingRight: 8,
    gap: 8,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    ...Platform.select({ web: { boxShadow: '0 4px 12px rgba(0,0,0,0.07)' } as any }),
      },
      android: { elevation: 3 },
    }),
  },

  iconWrap: {
    width: 20,
    alignItems: "center",
    justifyContent: "center",
  },

  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 15,
    fontWeight: "500",
    fontFamily: FontFamily.sans,
    paddingVertical: 12,
    backgroundColor: "transparent",
    ...(Platform.OS === "web" ? {
      outlineWidth: 0,
      outlineStyle: "none",
      borderWidth: 0,
    } as any : {}),
  },

  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  clearBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
  },

  divider: {
    width: 1,
    height: 18,
    borderRadius: 1,
  },

  submitBtn: {
    width: 34,
    height: 34,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    ...Platform.select({
      ios: {
        shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0,
    shadowRadius: 8,
    ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0)' } as any }),
      },
    }),
  },
});
